import { FormControl, FormGroup } from '@angular/forms';
import { Namespace, PromptModule } from '@vendasta/protos/ai_assistants/v1alpha1';
import { FormArrayWithDirtyTracking } from './forms';
import { createTemplateValidator } from './forms.validators';

// Prompt Module
export type AiPromptModuleControls = {
  id: FormControl<string | null>;
  namespace: FormControl<Namespace | null>;
  instructions: FormControl<string | null>;
  managed: FormControl<boolean>;
};

export class AiPromptModuleForm extends FormGroup<AiPromptModuleControls> {
  private originalContents: string | undefined;

  constructor(opts: { promptModule?: Partial<PromptModule>; contents?: string }) {
    const { promptModule, contents } = opts;

    super({
      id: new FormControl({ value: promptModule?.id ?? null, disabled: true }),
      namespace: new FormControl({ value: promptModule?.namespace ?? null, disabled: true }),
      instructions: new FormControl(contents ?? null, {
        asyncValidators: [createTemplateValidator()],
      }),
      managed: new FormControl({ value: promptModule?.managed ?? false, disabled: true }, { nonNullable: true }),
    });
    this.originalContents = contents;
  }

  override get dirty() {
    const currentValue = this.controls.instructions.getRawValue();
    // Treat falsy values the same (yes we could do != instead, but this way it's harder for someone to come along and change it by accident)
    return (currentValue || null) !== (this.originalContents || null);
  }

  override markAsPristine(): void {
    super.markAsPristine();
    const value = this.controls.instructions.getRawValue();
    this.originalContents = value === null ? undefined : value;
  }

  toPromptModule(localNamespace: Namespace, name?: string | null): Partial<PromptModule> {
    return {
      id: this.controls.id.getRawValue() ?? undefined,
      name: name ?? undefined,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      managed: this.controls.managed.getRawValue() ?? false,
    };
  }

  getContent(): string | undefined {
    return this.controls.instructions.getRawValue() ?? undefined;
  }

  /**
   * The content this module was loaded with — what `dirty` compares against, and so what "no unsaved change to this
   * prompt" means. Reading it is how a caller putting the loaded configuration back knows what to put.
   */
  getOriginalContent(): string | undefined {
    return this.originalContents;
  }

  /** What identifies this module, for a save that has to put it back after a failed call. */
  snapshotIdentity(): AiPromptModuleIdentity {
    return {
      id: this.controls.id.getRawValue(),
      namespace: this.controls.namespace.getRawValue(),
      managed: this.controls.managed.getRawValue(),
    };
  }

  restoreIdentity(identity: AiPromptModuleIdentity): void {
    this.controls.id.setValue(identity.id);
    this.controls.namespace.setValue(identity.namespace);
    this.controls.managed.setValue(identity.managed);
  }

  isGlobal(): boolean {
    return !!this.controls.namespace.getRawValue()?.globalNamespace;
  }

  /** Clones a global or managed module into the local namespace, as a module the save creates rather than edits. */
  forkInto(localNamespace: Namespace): void {
    this.controls.namespace.setValue(localNamespace);
    this.controls.managed.setValue(false);
    this.controls.id.setValue(null);
  }

  /** Stands the module up under the ID a save is sending it as, or the one the save assigned it. */
  assignId(id: string | null): void {
    this.controls.id.setValue(id);
  }
}

/** The identity controls a save's fork and placeholder mutations overwrite. */
export interface AiPromptModuleIdentity {
  id: string | null;
  namespace: Namespace | null;
  managed: boolean;
}

export class AiPromptModuleFormArray extends FormArrayWithDirtyTracking<AiPromptModuleForm> {
  constructor(opts: { promptModules: Partial<PromptModule>[]; contents: Record<string, string | undefined> }) {
    const { promptModules, contents } = opts;

    const controls = promptModules.map((pm) => {
      const content = pm.id ? contents[pm.id] : undefined;
      return new AiPromptModuleForm({ promptModule: pm, contents: content });
    });

    if (controls.length === 0) {
      controls.push(new AiPromptModuleForm({}));
    }
    super(controls);
  }

  getPromptModuleKeys(fallbackNamespace: Namespace): Partial<PromptModule>[] {
    return this.controls
      .map((pm) => {
        return {
          id: pm.controls.id.getRawValue() ?? undefined,
          namespace: pm.controls.namespace.getRawValue() ?? fallbackNamespace,
        };
      })
      .filter((pm) => !!pm.id);
  }
}
